/**
 * SignIn Component - Customer App
 *
 * Branded sign-in page for CFleet Customer Portal.
 * Integrates with @cfleet/auth for authentication using customer endpoints.
 *
 * Features:
 * - Email/password login via useAuth().login()
 * - "Remember me" checkbox for extended 30-day sessions
 * - Shows success message when redirected from password reset or invite acceptance
 * - Pre-fills email when coming from password reset
 * - Links to forgot password page
 * - "Joining via invitation?" button for new customers
 * - Customer-specific branding
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@cfleet/auth';
import { resolveReturnPath } from '../utils/returnTo';
import {
  Command,
  ArrowRight,
  Lock,
  Mail,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  UserPlus,
} from 'lucide-react';

interface LocationState {
  from?: { pathname?: string; search?: string; hash?: string };
  returnTo?: string;
  passwordReset?: boolean;
  inviteAccepted?: boolean;
  email?: string;
}

const SignIn: React.FC = () => {
  const { login, error, isLoading, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Default remember-me on: customers visit infrequently, so a returning
  // customer should stay signed in rather than be bounced to login. They can
  // still opt out (e.g. a shared device) by unchecking the box.
  const [rememberMe, setRememberMe] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Extract state from location
  const state = location.state as LocationState | null;
  // Restore the full intended destination (path + query + hash) after re-auth,
  // preferring an explicit returnTo. Guards against open-redirects.
  const from = resolveReturnPath(state);

  // Handle success messages and pre-fill email from password reset
  useEffect(() => {
    if (state?.passwordReset) {
      setSuccessMessage('Password reset successfully! You can now sign in.');
      if (state.email) {
        setEmail(state.email);
      }
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    } else if (state?.inviteAccepted) {
      setSuccessMessage('Account setup complete! You can now sign in.');
      if (state.email) {
        setEmail(state.email);
      }
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccessMessage(null);

    try {
      await login({ email, password, rememberMe });
      // Navigation will happen via the useEffect when isAuthenticated becomes true
    } catch {
      // Error is handled by AuthProvider and available via error state
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans text-zinc-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
        <div className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
                <Command size={20} />
              </div>
              <span className="font-bold text-3xl text-zinc-800 tracking-tight">cfleet</span>
            </div>
          </div>

          <div className="mb-8 text-center">
            <h2 className="text-xl font-bold text-zinc-900">Welcome back</h2>
            <p className="text-sm text-zinc-500 mt-2">Sign in to manage your deliveries</p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
              <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-700">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-zinc-700 mb-1.5">
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-blue-500 transition-colors">
                  <Mail size={18} />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-zinc-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="name@company.com"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label htmlFor="password" className="block text-xs font-medium text-zinc-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-blue-500 transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-zinc-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
                className="w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="remember-me" className="ml-2 text-sm text-zinc-600">
                Remember me for 30 days
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-zinc-900 text-white py-3 rounded-lg text-sm font-semibold shadow-lg hover:bg-zinc-800 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-lg"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign In <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Invitation Button - for customers joining via invitation */}
          <div className="mt-6 pt-6 border-t border-zinc-100">
            <Link
              to="/accept-invite"
              className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-zinc-50 border border-zinc-200 text-zinc-600 text-xs font-medium hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all"
            >
              <UserPlus size={14} /> Joining via invitation?
            </Link>
          </div>
        </div>

        <div className="px-8 py-5 bg-zinc-50 border-t border-zinc-100 text-center">
          <p className="text-xs text-zinc-500">
            Don't have an account?{' '}
            <a href="#" className="font-medium text-blue-600 hover:text-blue-700">
              Contact your administrator
            </a>
          </p>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Secure Connection
        </div>
        <p className="text-xs text-zinc-400">© 2025 cfleet. All rights reserved.</p>
      </div>
    </div>
  );
};

export default SignIn;
